import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import RegureSidebar from '../RegureSidebar';
import ScreenLoader from '../ScreenLoader';
import { getLoggedinUserAction } from '../../redux/actions/userActions';

// ─── New App Layout ───────────────────────────────────────
// Used by all new Regure pages (Dashboard, Claims, Policies, etc.)
// Provides sidebar + scrollable main content area.
// Auth-gates to /login if user is not authenticated.
const AppLayout = ({ children }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userLogin = useSelector(state => state.userLogin);

  const isLoggedIn = userLogin?.userInfo?.status === 'success';
  const hasUserData = !!(userLogin?.userInfo?.data?.user || userLogin?.userInfo?.data?.data);

  // Latch: once the user object has loaded, remember it for the lifetime of the
  // mount. A later transient dip in `hasUserData` — e.g. a background
  // getLoggedinUserAction refresh that briefly clears the user in flight —
  // must NOT swap the whole app for the loader below, because doing so
  // UNMOUNTS every open screen and side panel and silently destroys any
  // in-progress work (a New Offer quote being the reported case). The loader is
  // only for the genuine first load, before the user has ever been available.
  const everHadUserDataRef = useRef(false);
  if (hasUserData) everHadUserDataRef.current = true;

  useEffect(() => {
    if (!isLoggedIn) {
      history.push('/login');
    } else {
      // Clear "Sign In" title once user is in the app
      if (document.title === 'Regure — Sign In') document.title = 'Regure';
      // After a page reload, localStorage only has { status, token } — user data isn't populated.
      // Fetch it so the socket can connect and all screens have the user object available.
      // If the cookie is dead, the 401 interceptor will clear localStorage and redirect to login.
      // Previously guarded on `hasUserData` — inverted — so hard refresh spun forever on the
      // loading placeholder because the fetch that would populate userData never fired.
      // BUILD-MARKER: cache-bust-2026-08-06-1
      if (!hasUserData) {
        dispatch(getLoggedinUserAction());
      }
    }
  }, [isLoggedIn, hasUserData, history, dispatch]);

  // Show the loader only on the genuine first load — never after the user has
  // already been available once (see everHadUserDataRef above), so a transient
  // refresh cannot tear down open screens/panels mid-edit.
  if (isLoggedIn && !hasUserData && !everHadUserDataRef.current) return <ScreenLoader />;

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Inter', sans-serif",
      background: 'linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%)',
      color: '#1e293b',
      overflow: 'hidden',
    }}>
      <RegureSidebar />
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
      }}>
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
